import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, Users, Handshake } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { name: "", role_it: "", role_en: "", bio_it: "", bio_en: "", image_url: "", link: "", kind: "team", order: 0, active: true };

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("team");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/admin/team").then((r) => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((i) => i.kind === tab), [items, tab]);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/team/${editing.id}`, editing);
        toast.success("Aggiornato");
      } else {
        await api.post("/admin/team", editing);
        toast.success("Creato");
      }
      setEditing(null);
      load();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Confermi l'eliminazione?")) return;
    await api.delete(`/admin/team/${id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-team-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Chi siamo</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Team & Collaborazioni</h1>
          <p className="text-[#0A0A0A]/60 mt-2 max-w-xl">Gestisci i membri del team e i partner mostrati nella pagina Chi siamo.</p>
        </div>
        <button className="btn-cta" onClick={() => setEditing({ ...EMPTY, kind: tab })} data-testid="admin-team-add">
          <Plus size={16} /> Aggiungi {tab === "team" ? "membro" : "partner"}
        </button>
      </div>

      <div className="mb-6 flex gap-2 border-b-2 border-[#0A0A0A]">
        <button
          onClick={() => setTab("team")}
          data-testid="admin-team-tab-team"
          className={`px-6 py-3 -mb-[2px] border-b-4 text-sm font-bold uppercase tracking-widest flex items-center gap-2 ${tab === "team" ? "border-[#FF5A1F] text-[#0A0A0A]" : "border-transparent text-[#0A0A0A]/50"}`}
        >
          <Users size={14} /> Team ({items.filter((i) => i.kind === "team").length})
        </button>
        <button
          onClick={() => setTab("partner")}
          data-testid="admin-team-tab-partner"
          className={`px-6 py-3 -mb-[2px] border-b-4 text-sm font-bold uppercase tracking-widest flex items-center gap-2 ${tab === "partner" ? "border-[#FF5A1F] text-[#0A0A0A]" : "border-transparent text-[#0A0A0A]/50"}`}
        >
          <Handshake size={14} /> Partner ({items.filter((i) => i.kind === "partner").length})
        </button>
      </div>

      {loading ? <div className="text-[#0A0A0A]/50">Caricamento…</div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((m) => (
            <div key={m.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7]" data-testid={`admin-team-item-${m.id}`}>
              <div className="aspect-video bg-[#F2F2EC] overflow-hidden flex items-center justify-center">
                {m.image_url ? (
                  <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="font-display text-4xl text-[#0A0A0A]/30 uppercase">{m.name}</div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">{m.role_it}</div>
                  {!m.active && <span className="text-[10px] uppercase text-[#0A0A0A]/40">Nascosto</span>}
                </div>
                <h3 className="font-display text-2xl uppercase mt-1">{m.name}</h3>
                {m.bio_it && <p className="mt-2 text-sm text-[#0A0A0A]/70 line-clamp-2">{m.bio_it}</p>}
                <div className="mt-4 flex gap-2">
                  <button
                    className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest flex items-center gap-1 hover:bg-[#0A0A0A] hover:text-[#FAFAF7]"
                    onClick={() => setEditing(m)}
                    data-testid={`admin-team-edit-${m.id}`}
                  >
                    <Pencil size={12} /> Modifica
                  </button>
                  <button
                    className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest flex items-center gap-1 hover:bg-red-600 hover:text-white hover:border-red-600"
                    onClick={() => remove(m.id)}
                    data-testid={`admin-team-delete-${m.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <TeamEditor value={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

const TeamEditor = ({ value, onChange, onSave, onClose }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });

  const uploadImg = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Max 3 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("image_url", reader.result);
    reader.readAsDataURL(f);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-team-editor">
      <div className="bg-[#FAFAF7] max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex items-center justify-between">
          <h3 className="font-display text-2xl uppercase">{value.id ? "Modifica" : "Nuovo"} {value.kind === "team" ? "membro" : "partner"}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs uppercase font-bold tracking-widest text-[#0A0A0A]/60">Tipo</span>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => set("kind", "team")} className={`px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase font-bold tracking-widest ${value.kind === "team" ? "bg-[#0A0A0A] text-[#FAFAF7]" : ""}`} data-testid="team-kind-team">
                Team
              </button>
              <button type="button" onClick={() => set("kind", "partner")} className={`px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase font-bold tracking-widest ${value.kind === "partner" ? "bg-[#0A0A0A] text-[#FAFAF7]" : ""}`} data-testid="team-kind-partner">
                Partner
              </button>
            </div>
          </label>

          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Nome" value={value.name} onChange={(v) => set("name", v)} testId="team-name" />
            <Input label="Ordine" type="number" value={value.order} onChange={(v) => set("order", parseInt(v) || 0)} testId="team-order" />
            <Input label="Ruolo (IT)" value={value.role_it} onChange={(v) => set("role_it", v)} testId="team-role-it" />
            <Input label="Ruolo (EN)" value={value.role_en} onChange={(v) => set("role_en", v)} testId="team-role-en" />
          </div>

          <TextArea label="Bio (IT)" value={value.bio_it} onChange={(v) => set("bio_it", v)} testId="team-bio-it" />
          <TextArea label="Bio (EN)" value={value.bio_en} onChange={(v) => set("bio_en", v)} testId="team-bio-en" />

          <Input label="Link esterno (opzionale)" value={value.link} onChange={(v) => set("link", v)} placeholder="https://..." testId="team-link" />

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">Immagine / Logo (URL o upload)</label>
            <input type="text" value={value.image_url?.startsWith("data:") ? "" : value.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://... (partner: lascia vuoto per solo testo)" className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white" data-testid="team-image-url" />
            <label className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold cursor-pointer border-2 border-[#0A0A0A] px-3 py-2 hover:bg-[#0A0A0A] hover:text-[#FAFAF7]">
              <Upload size={14} /> Upload
              <input type="file" accept="image/*" className="hidden" onChange={uploadImg} data-testid="team-image-file" />
            </label>
            {value.image_url && <img src={value.image_url} alt="preview" className="mt-3 h-32 object-cover border-2 border-[#0A0A0A]" />}
          </div>

          <label className="flex items-center gap-2">
            <input type="checkbox" checked={value.active} onChange={(e) => set("active", e.target.checked)} data-testid="team-active" />
            <span className="text-sm">Visibile sul sito</span>
          </label>
        </div>
        <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline-dark" data-testid="admin-team-cancel">Annulla</button>
          <button onClick={onSave} className="btn-cta" data-testid="admin-team-save">Salva</button>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", testId, placeholder }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white" />
  </label>
);
const TextArea = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId} className="mt-2 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white resize-none" />
  </label>
);
